import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowTopRightOnSquareIcon, PencilIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import toast from "react-hot-toast";
import { CREATE_ADVERTISER, GET_ADVERTISERS, UPDATE_ADVERTISER } from "../../../utils/api/advertiser";
import { useModal, useQueryVariables } from "../../../utils/hooks";
import Table from '../../../components/lists/Table/Table';
import SettingsCard from "./SettingsCard";
import AddAdvertiserModal from "../../../components/modals/AddAdvertiserModal";
import { AdvancedImage, placeholder } from "@cloudinary/react";
import {
  cld,
} from "../../../utils/cloudinary";
import { thumbnail } from "@cloudinary/url-gen/actions/resize";
import ActionButton, { ActionHandler } from "../../../components/lists/Table/shared/ActionButton";
import FormModal from "../../../components/modals/FormModal";
import SingleLineField from "../../../components/forms/SingleLineField";
import ImageUploadField from '../../../components/forms/ImageUploadField';


const getColumns = (onEdit: ActionHandler<any>) => [
  {
    dataIndex: "name",
    title: "Name",
    key: "name",
  },
  {
    dataIndex: "_id",
    title: "ID",
    key: "id",
    render: (id: string) => {
      const copyToClipboard = () => {
        navigator.clipboard.writeText(id);
        toast.success("Advertiser ID copied to clipboard!");
      }
      return (
        <div className="flex items-center space-x-2">
          <span className="text-gray-500 font-mono text-sm">{id}</span>
          <Tooltip title="Copy ID">
            <button type="button" onClick={copyToClipboard} className="text-gray-400 hover:text-gray-600 focus:outline-none">
              <DocumentDuplicateIcon className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      );
    }
  },
  {
    dataIndex: "image",
    title: "Logo",
    key: "image",
    render: (imageId: string) => {
      return <AdvancedImage cldImg={cld.image(imageId).resize(thumbnail().height(30)).format("png")} plugins={[placeholder()]} />
    }
  },
  {
    dataIndex: "country",
    title: "Country",
    key: "country",
  },
  {
    dataIndex: "_id",
    title: "Billing",
    key: "_id",
    render: (id: string) => {
      return <Link to={`/settings/billing?advertiserid=${id}`}
        target="_blank" rel="noopener noreferrer"
      >
        <Tooltip
          placement="bottom"
          title="Go to billing"
        >
          <ArrowTopRightOnSquareIcon className="h-6 w-6 text-gray-400 hover:text-gray-500" />
        </Tooltip>
      </Link>
    }
  },
  {
    dataIndex: "actions",
    key:"actions",
    render: (_: any, record: any) => <div className="px-3">
    <ActionButton tooltip="Edit Advertiser" row={record} onClick={onEdit} Icon={PencilIcon} />
  </div>,

  }
]

const CreateAdvertiser = () => {
  const {variables, setPagination, setFilters} = useQueryVariables();
  const { loading, data } = useQuery(GET_ADVERTISERS, {
    variables
  })

  const [createAdvertiser] = useMutation(CREATE_ADVERTISER, {
    refetchQueries: ['AdvertiserMany']
  })

  const [updateAdvertiser] = useMutation(UPDATE_ADVERTISER, {
    refetchQueries: ['AdvertiserMany']
  })

  const [modalVisible, showModal, hideModal] = useModal()
  const [editData, setEditData] = useState(null);

  const handleCreateAdvertiser = (advertiser: any) => {
    createAdvertiser({variables: {record: advertiser}})
  }
  const handleEdit = (record: any) => {
    const {name, image} = record
    updateAdvertiser({variables: { record: {name, image}, id: record._id}})
  }
  return (
    <SettingsCard
      title="Advertisers"
      description="A list of all the advertisers including their name and country."
    >
      <Table
        className="mt-8"
        columns={getColumns(setEditData)}
        data={data?.advertisers.items || []}
        actionButtonText='Add Advertiser'
        onActionButtonClick={showModal}
        pagination={{
          total: data ? data.advertisers.meta.totalItems : 0,
          pageSize: variables.perPage,
          current: variables.page
        }}
        onChange={(pagination, filters) =>{
          setPagination(pagination)
          setFilters(filters)
        }}
        loading={loading}
      />

      <AddAdvertiserModal
        open={modalVisible}
        onCancel={hideModal}
        onOk={handleCreateAdvertiser}
      />
      {editData && <FormModal
        open
        defaultValues={editData}
        title="Edit Advertiser"
        okText="Save"
        onCancel={() => setEditData(null)}
        onOk={handleEdit}
      >
        <SingleLineField
          label="Name"
          top
          required
          htmlfor="name" />
        <ImageUploadField name="image" label="Logo"/>
      </FormModal>}
    </SettingsCard>
  );
};

export default CreateAdvertiser;
